/**
 * Shared DOM-based result overlay used by every game on game-over and win.
 *
 * Visual structure (top → bottom):
 *
 *   ┌──────────────────────────────────────┐
 *   │  ★   TITLE   ★                       │
 *   │  ┌────────────────────────────────┐  │
 *   │  │  icon   icon   icon            │  │
 *   │  │  LABEL  LABEL  LABEL           │  │
 *   │  │  value  value  value           │  │
 *   │  └────────────────────────────────┘  │
 *   │       ENCOURAGEMENT PILL             │
 *   │    [home]    [retry]    [next]       │
 *   └──────────────────────────────────────┘
 *
 * Two call styles are supported:
 *
 *   // Rich (new) — preferred:
 *   overlay.show(w, h, {
 *     title: 'JÁTÉK VÉGE',
 *     won: false,
 *     subtitle: 'ÜGYES VOLTÁL! PRÓBÁLD ÚJRA!',
 *     stats: [
 *       { icon: '/assets/.../cup.png', label: 'PONT', value: 120 },
 *       { icon: '/assets/.../coin.png', label: 'ÉRMÉK', value: 12 },
 *       { icon: '/assets/.../star.png', label: 'CSILLAGOK', value: 2 },
 *     ],
 *     actions: [
 *       { kind: 'home',  label: 'FŐMENÜ', onClick: () => goMenu() },
 *       { kind: 'retry', label: 'ÚJRA',   onClick: () => restart(), primary: true },
 *       { kind: 'next',  label: 'TOVÁBB', onClick: () => nextScreen() },
 *     ],
 *   });
 *
 *   // Legacy (Snake/Pipeline still pass this form):
 *   overlay.show(w, h, { title, lines });
 *
 * The root has `pointer-events:none` by default; only the action buttons
 * re-enable pointer events. That way the underlying scene's tap-to-restart
 * input still works when no actions are provided.
 */
import { Container } from '../../vendor/pixi.min.mjs';
import { t } from '../config/i18n.js';
import { injectOverlayVars } from './overlayPalette.js';

const ICON_SVGS = {
    home:  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 11.5L12 4l9 7.5"/><path d="M5 10v9h14v-9"/><path d="M9.5 19v-5h5v5"/></svg>',
    retry: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"><path d="M20 4v6h-6"/><path d="M20 10A9 9 0 1 0 17.6 17"/></svg>',
    next:  '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M7 4l13 8-13 8z"/></svg>',
};

/**
 * Reusable inline SVG icons for the stats cards. Use any of these as
 * `stats[i].icon` when calling {@link GameOverOverlay#show} and the SVG
 * will render in the accent-gold colour (inherits via `currentColor`).
 */
export const STAT_ICONS = {
    trophy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 4h8v4a4 4 0 0 1-8 0V4Z"/><path d="M8 5H5v3a3 3 0 0 0 3 3"/><path d="M16 5h3v3a3 3 0 0 1-3 3"/><path d="M10 14h4v4h-4z"/><path d="M8 21h8"/><path d="M12 18v3"/></svg>',
    flag:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M5 21V4"/><path d="M5 4h12l-2.5 4L17 12H5"/></svg>',
    tram:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="4" y="5" width="16" height="12" rx="2.5"/><line x1="4" y1="11" x2="20" y2="11"/><circle cx="8.5" cy="19" r="1.4" fill="currentColor"/><circle cx="15.5" cy="19" r="1.4" fill="currentColor"/></svg>',
    star:   '<svg viewBox="0 0 24 24" fill="currentColor" stroke="none"><path d="M12 2.5l2.95 6.36 7.05.78-5.25 4.92 1.48 6.94L12 17.9l-6.23 3.6 1.48-6.94L2 9.64l7.05-.78L12 2.5z"/></svg>',
    coin:   '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9" fill="currentColor" fill-opacity="0.18"/><circle cx="12" cy="12" r="9"/><path d="M9.5 9.5h3.5a2 2 0 0 1 0 4H10v2h3.5a2 2 0 0 0 0-4"/></svg>',
};

let _stylesInjected = false;
function injectStyles() {
    if (_stylesInjected) return;
    _stylesInjected = true;
    injectOverlayVars();
    const css = `
    .gov-root { position:fixed; inset:0; z-index:9000; display:flex; align-items:center; justify-content:center;
      background:var(--ov-backdrop); pointer-events:none;
      font-family:'Poppins','Inter','Segoe UI',Arial,sans-serif; }
    .gov-panel { background:var(--ov-panel-bg);
      border:var(--ov-panel-border-width) solid var(--ov-panel-border);
      /* Sharp corners — per UX direction every popup modal + the
         buttons inside drop their rounded edges. */
      border-radius:0; padding:38px 46px 34px; min-width:520px; max-width:760px;
      box-shadow:0 24px 60px rgba(0,0,0,0.45);
      text-align:center; pointer-events:auto; }
    .gov-title-row { display:flex; align-items:center; justify-content:center; gap:24px; margin:0 0 22px; }
    .gov-star { color:var(--ov-star); font-size:42px; line-height:1; filter:drop-shadow(0 2px 0 rgba(0,0,0,0.18)); }
    .gov-title { font-size:54px; font-weight:900; color:var(--ov-title); letter-spacing:1.5px;
      text-shadow:0 3px 0 var(--ov-title-shadow); }
    .gov-stats { background:var(--ov-card-bg);
      border-radius:0; padding:22px 18px; display:flex; justify-content:space-around; gap:14px;
      margin-bottom:18px; }
    .gov-stat { display:flex; flex-direction:column; align-items:center; gap:8px; min-width:130px; }
    .gov-stat-icon { width:74px; height:74px; display:flex; align-items:center; justify-content:center;
      color:var(--ov-accent-gold); }
    .gov-stat-icon img { max-width:100%; max-height:100%; object-fit:contain;
      filter:drop-shadow(0 3px 0 rgba(0,0,0,0.25)); }
    .gov-stat-icon svg { width:100%; height:100%;
      filter:drop-shadow(0 3px 0 rgba(0,0,0,0.30)); }
    .gov-stat-label { font-size:18px; font-weight:700; color:var(--ov-label); letter-spacing:1.2px; }
    .gov-stat-value { font-size:38px; font-weight:900; color:var(--ov-value);
      text-shadow:0 2px 0 var(--ov-value-shadow); }
    .gov-subtitle-wrap { text-align:center; margin-bottom:20px; }
    .gov-subtitle { display:inline-block; background:var(--ov-subtitle-bg);
      color:var(--ov-subtitle-fg);
      font-size:18px; font-weight:800; letter-spacing:0.6px;
      padding:10px 22px; border-radius:0; }
    .gov-actions { display:flex; align-items:stretch; justify-content:center; gap:14px; }
    .gov-btn { border:none; cursor:pointer; font-family:inherit; font-weight:900;
      letter-spacing:1px; transition:transform 0.12s ease, filter 0.12s ease;
      display:flex; flex-direction:column; align-items:center; justify-content:center;
      padding:14px 18px; gap:6px; box-shadow:0 6px 0 rgba(0,0,0,0.18); }
    .gov-btn:hover { transform:translateY(-2px); filter:brightness(1.05); }
    .gov-btn:active { transform:translateY(1px); box-shadow:0 3px 0 rgba(0,0,0,0.18); }
    .gov-btn-square { width:128px; height:108px; border-radius:0; }
    .gov-btn-square .gov-btn-icon { width:38px; height:38px; }
    .gov-btn-square .gov-btn-label { font-size:18px; }
    .gov-btn-wide { min-width:230px; height:108px; border-radius:0;
      flex-direction:row; padding:0 26px; gap:14px; }
    .gov-btn-wide .gov-btn-icon { width:44px; height:44px; }
    .gov-btn-wide .gov-btn-label { font-size:28px; }
    .gov-btn-home  { background:var(--ov-btn-home-bg);  color:var(--ov-btn-home-fg);
                     border-bottom:5px solid var(--ov-btn-home-edge); }
    .gov-btn-retry { background:var(--ov-btn-retry-bg); color:var(--ov-btn-retry-fg);
                     border-bottom:5px solid var(--ov-btn-retry-edge); }
    .gov-btn-next  { background:var(--ov-btn-next-bg);  color:var(--ov-btn-next-fg);
                     border-bottom:5px solid var(--ov-btn-next-edge); }
    .gov-btn-icon { display:flex; align-items:center; justify-content:center; }
    .gov-btn-icon svg { width:100%; height:100%; }
    .gov-tap { color:var(--ov-subtitle-fg); font-size:22px; font-weight:700; margin-top:4px; }
    .gov-fallback-body { color:var(--ov-value); font-size:24px; font-weight:700;
      line-height:1.5; white-space:pre-line; margin:0 0 18px; }
    `;
    const style = document.createElement('style');
    style.dataset.gov = '1';
    style.textContent = css;
    document.head.appendChild(style);
}

export class GameOverOverlay {
    container = new Container();
    /** @type {HTMLDivElement|null} */
    _root = null;
    /** @type {HTMLDivElement|null} */
    _tapEl = null;
    _timer = 0;

    show(_w, _h, opts = {}) {
        injectStyles();
        const {
            title,
            won = false,
            stats,
            actions,
            subtitle,
            lines = [],
        } = opts;

        const root = document.createElement('div');
        root.className = 'gov-root';

        const panel = document.createElement('div');
        panel.className = 'gov-panel';
        root.appendChild(panel);

        const titleRow = document.createElement('div');
        titleRow.className = 'gov-title-row';
        titleRow.innerHTML = `
            <span class="gov-star">★</span>
            <span class="gov-title">${escapeHtml(title ?? t(won ? 'common.you_win' : 'common.game_over'))}</span>
            <span class="gov-star">★</span>
        `;
        panel.appendChild(titleRow);

        if (Array.isArray(stats) && stats.length) {
            panel.appendChild(buildStats(stats));
        }

        const subtitleText = subtitle ?? t(won ? 'common.encourage_win' : 'common.encourage_lose');
        if (subtitleText) {
            const wrapper = document.createElement('div');
            wrapper.className = 'gov-subtitle-wrap';
            const sub = document.createElement('div');
            sub.className = 'gov-subtitle';
            sub.textContent = subtitleText;
            wrapper.appendChild(sub);
            panel.appendChild(wrapper);
        }

        if (Array.isArray(actions) && actions.length) {
            panel.appendChild(buildActions(actions, root));
        } else if (lines.length) {
            const body = document.createElement('div');
            body.className = 'gov-fallback-body';
            body.textContent = lines.join('\n');
            panel.appendChild(body);

            const tap = document.createElement('div');
            tap.className = 'gov-tap';
            tap.textContent = t('common.tap_restart');
            panel.appendChild(tap);
            this._tapEl = tap;
        }

        document.body.appendChild(root);
        this._root = root;
        this._timer = 0;
    }

    update(delta) {
        this._timer += delta / 60;
        if (this._tapEl) {
            this._tapEl.style.opacity = String(0.7 + 0.3 * Math.sin(this._timer * 3));
        }
    }

    destroy() {
        if (this._root) {
            this._root.remove();
            this._root = null;
        }
        this._tapEl = null;
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this.container.removeChildren?.();
    }
}

function buildStats(stats) {
    const wrap = document.createElement('div');
    wrap.className = 'gov-stats';
    for (const s of stats) {
        const col = document.createElement('div');
        col.className = 'gov-stat';

        const icon = document.createElement('div');
        icon.className = 'gov-stat-icon';
        if (s.icon) {
            // Accept either:
            //   • a URL string  → rendered via <img src=…>
            //   • a raw SVG markup string  → injected as innerHTML so the
            //     SVG inherits CSS color via `currentColor` (matches the
            //     home/retry/next action-button icon convention).
            const raw = String(s.icon).trim();
            if (raw.startsWith('<')) {
                icon.innerHTML = raw;
            } else {
                const img = document.createElement('img');
                img.alt = '';
                img.src = raw;
                icon.appendChild(img);
            }
        }
        col.appendChild(icon);

        const label = document.createElement('div');
        label.className = 'gov-stat-label';
        label.textContent = s.label ?? '';
        col.appendChild(label);

        const value = document.createElement('div');
        value.className = 'gov-stat-value';
        value.textContent = String(s.value ?? '');
        col.appendChild(value);

        wrap.appendChild(col);
    }
    return wrap;
}

function buildActions(actions, root) {
    const wrap = document.createElement('div');
    wrap.className = 'gov-actions';
    for (const a of actions) {
        const btn = document.createElement('button');
        const kind = a.kind ?? 'retry';
        const wide = a.primary || kind === 'retry';
        btn.type = 'button';
        btn.className = `gov-btn ${wide ? 'gov-btn-wide' : 'gov-btn-square'} gov-btn-${kind}`;

        const ic = document.createElement('span');
        ic.className = 'gov-btn-icon';
        ic.innerHTML = ICON_SVGS[kind] || ICON_SVGS.next;
        btn.appendChild(ic);

        const label = document.createElement('span');
        label.className = 'gov-btn-label';
        label.textContent = a.label ?? '';
        btn.appendChild(label);

        btn.addEventListener('click', (ev) => {
            ev.preventDefault();
            ev.stopPropagation();
            try { a.onClick?.(); } catch (err) { console.warn('[GameOverOverlay] action failed:', err); }
            if (root && root.parentNode) {
                root.remove();
            }
        });

        wrap.appendChild(btn);
    }
    return wrap;
}

function escapeHtml(s) {
    return String(s ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}
